import React, { useState } from 'react';

const TermsAndServicesPage = () => {
  const [activeSection, setActiveSection] = useState('intro');

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  return (
    <div className="min-h-screen bg-_white text-_dark mt-40">
      {/* Header */}
      <header className="bg-_dark text-_white py-6 hidden">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl md:text-3xl font-bold">Terms and Services</h1>
          <p className="text-_gray_lite mt-2">Last Updated: April 5, 2025</p>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 flex flex-col md:flex-row gap-8">
        {/* Navigation Sidebar */}
        <aside className="md:w-1/4 shrink-0">
          <div className="bg-_gray_lite p-4 rounded-lg sticky top-28">
            <h2 className="text-_blue font-bold text-lg mb-4">Contents</h2>
            <nav>
              <ul className="space-y-2">
                {[
                  { id: 'intro', label: 'Introduction' },
                  { id: 'definitions', label: 'Definitions' },
                  { id: 'account', label: 'Your Account' },
                  { id: 'orders', label: 'Orders and Payments' },
                  { id: 'shipping', label: 'Shipping and Delivery' },
                  { id: 'returns', label: 'Returns and Refunds' },
                  { id: 'intellectual', label: 'Intellectual Property' },
                  { id: 'privacy', label: 'Privacy Policy' },
                  { id: 'limitation', label: 'Limitation of Liability' },
                  { id: 'changes', label: 'Changes to Terms' },
                ].map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                        activeSection === section.id
                          ? 'bg-_blue text-_white'
                          : 'hover:bg-_gray_hover text-_dark'
                      }`}
                    >
                      {section.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="md:w-3/4">
          <div className="bg-_white rounded-lg shadow-sm border border-_gray_lite p-6">
            <section id="intro" className="mb-8">
              <h2 className="text-2xl font-bold text-_blue mb-4">Introduction</h2>
              <p className="mb-4">
                Welcome to MedtoolsLK, your trusted medical equipment platform. These Terms and Services govern your use of our website, including any content, functionality, and services offered on or through medtools.lk. By using our website, you accept and agree to be bound by these Terms and Services.
              </p>
              <p>
                Please read these Terms and Services carefully before you start using our website. If you do not agree to these Terms and Services, you must not access or use our website. MedtoolsLK provides access to quality medical equipment for healthcare professionals and patients alike.
              </p>
            </section>

            <section id="definitions" className="mb-8">
              <h2 className="text-2xl font-bold text-_blue mb-4">Definitions</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>"We," "Us," "Our," "MedtoolsLK"</strong> refers to the company operating medtools.lk.</li>
                <li><strong>"Website"</strong> refers to MedtoolsLK, accessible from medtools.lk and all subdomains.</li>
                <li><strong>"You," "Your"</strong> refers to the individual accessing or using the Website, or the company or other legal entity on behalf of which such individual is accessing or using the Website.</li>
                <li><strong>"Medical Equipment"</strong> refers to the healthcare and medical items offered for sale on the Website.</li>
                <li><strong>"Services"</strong> refers to any services offered through the Website, including equipment consultation, installation guidance, and maintenance support.</li>
                <li><strong>"Content"</strong> refers to text, images, audio, video, and any other material displayed on the Website.</li>
              </ul>
            </section>

            <section id="account" className="mb-8">
              <h2 className="text-2xl font-bold text-_blue mb-4">Your Account</h2>
              <p className="mb-4">
                When you create an account with MedtoolsLK, you must provide information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms and Services, which may result in immediate termination of your account on our Website.
              </p>
              <p className="mb-4">
                You are responsible for safeguarding the password that you use to access the Website and for any activities or actions under your password. You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
              </p>
              <p>
                MedtoolsLK reserves the right to refuse service, terminate accounts, remove or edit content, or cancel orders in our sole discretion, especially in cases where medical equipment may be misused or misrepresented.
              </p>
            </section>

            <section id="orders" className="mb-8">
              <h2 className="text-2xl font-bold text-_blue mb-4">Orders and Payments</h2>
              <p className="mb-4">
                By placing an order through MedtoolsLK, you warrant that you are legally capable of entering into binding contracts and are at least 18 years old. For certain medical equipment, we may require verification of professional healthcare credentials.
              </p>
              <p className="mb-4">
                When you place an order, we will send you an email confirming receipt of your order. This email is only an acknowledgment and does not constitute acceptance of your order. Your order constitutes an offer to us to buy medical equipment. All orders are subject to acceptance by us, and we will confirm such acceptance to you by sending you an email confirming that the product has been dispatched.
              </p>
              <p className="mb-4">
                The contract between us will only be formed when we dispatch the medical equipment to you. We reserve the right to refuse an order for any reason, particularly for controlled medical devices that require special authorization.
              </p>
              <p>
                All payments are processed securely through our payment processors. We do not store your payment information on our servers. MedtoolsLK accepts various payment methods including credit cards, bank transfers, and healthcare institutional purchase orders.
              </p>
            </section>

            <section id="shipping" className="mb-8">
              <h2 className="text-2xl font-bold text-_blue mb-4">Shipping and Delivery</h2>
              <p className="mb-4">
                MedtoolsLK will make every effort to deliver medical equipment within the estimated timeframes; however, we cannot guarantee delivery times and shall not be liable for any losses, liabilities, costs, damages, charges, or expenses arising out of late delivery.
              </p>
              <p className="mb-4">
                Risk of loss and title for medical equipment purchased from MedtoolsLK pass to you upon delivery of the items to the carrier. You are responsible for filing any claims with carriers for damaged and/or lost shipments.
              </p>
              <p>
                Shipping costs are calculated based on the weight and dimensions of the medical equipment and the delivery destination. Special handling fees may apply for sensitive medical devices requiring temperature control or other special conditions. Shipping costs will be displayed during checkout before you complete your order.
              </p>
            </section>

            <section id="returns" className="mb-8">
              <h2 className="text-2xl font-bold text-_blue mb-4">Returns and Refunds</h2>
              <p className="mb-4">
                MedtoolsLK offers a 14-day return policy for most medical equipment. To be eligible for a return, the item must be unused, in its original packaging, and all seals must be intact. Due to the sensitive nature of medical equipment, we have strict return guidelines.
              </p>
              <p className="mb-4">
                To initiate a return, please contact our customer service team. If your return is approved, we will send you a return shipping label and instructions on how to safely package and send your medical equipment to our return facility.
              </p>
              <p className="mb-4">
                Refunds will be issued to the original payment method once we receive and inspect the returned item. Please allow 5-10 business days for the refund to be processed.
              </p>
              <p>
                Certain medical items are non-returnable due to health and hygiene reasons, or due to their custom nature. All sterile, single-use, or custom-configured medical equipment is non-returnable unless defective. These items will be clearly marked on the product page.
              </p>
            </section>

            <section id="intellectual" className="mb-8">
              <h2 className="text-2xl font-bold text-_blue mb-4">Intellectual Property</h2>
              <p className="mb-4">
                The MedtoolsLK website and its original content, features, and functionality are and will remain the exclusive property of MedtoolsLK and its licensors. The Website is protected by copyright, trademark, and other laws of both Sri Lanka and foreign countries.
              </p>
              <p className="mb-4">
                MedtoolsLK trademarks and trade dress may not be used in connection with any product or service without the prior written consent of our company.
              </p>
              <p>
                You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our Website, including medical equipment images, descriptions, or educational content, except as it is created and owned by you.
              </p>
            </section>

            <section id="privacy" className="mb-8">
              <h2 className="text-2xl font-bold text-_blue mb-4">Privacy Policy</h2>
              <p className="mb-4">
                Your use of MedtoolsLK is also governed by our Privacy Policy, which is incorporated into these Terms and Services by reference. Our Privacy Policy outlines how we collect, use, and protect your personal information, including any healthcare-related information you may share.
              </p>
              <p>
                By using MedtoolsLK, you consent to the collection and use of information as described in our Privacy Policy. We maintain strict compliance with healthcare privacy regulations and encourage you to review our Privacy Policy to understand our practices.
              </p>
            </section>

            <section id="limitation" className="mb-8">
              <h2 className="text-2xl font-bold text-_blue mb-4">Limitation of Liability</h2>
              <p className="mb-4">
                In no event shall MedtoolsLK, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your access to or use of or inability to access or use the Website;</li>
                <li>Any conduct or content of any third party on the Website;</li>
                <li>Any content obtained from the Website;</li>
                <li>Unauthorized access, use, or alteration of your transmissions or content;</li>
                <li>Improper use of medical equipment purchased through our Website; and</li>
                <li>Any medical decisions made based on information provided through our Website.</li>
              </ul>
              <p className="mt-4">
                MedtoolsLK provides product information for educational purposes only. We do not provide medical advice, and the use of medical equipment should always be under appropriate healthcare supervision.
              </p>
            </section>

            <section id="changes" className="mb-8">
              <h2 className="text-2xl font-bold text-_blue mb-4">Changes to Terms</h2>
              <p className="mb-4">
                MedtoolsLK reserves the right to modify or replace these Terms and Services at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect.
              </p>
              <p className="mb-4">
                By continuing to access or use MedtoolsLK after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the Website.
              </p>
              <p>
                It is your responsibility to review these Terms and Services periodically for changes, especially before purchasing medical equipment.
              </p>
            </section>


           
          </div>
        </main>
      </div>
    </div>
  );
};

export default TermsAndServicesPage;